import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FaClipboardCheck, FaMagic } from 'react-icons/fa';
import { trpc, trpcClient } from '@/api/trpc';
import { Card, Tabs, type Tab } from '@/components/UI';
import { showToast } from '@/components/Layout/Toast';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import AuditScopeControl from './parameterDiscoveryAudit/AuditScopeControl';
import AuditActionButtons from './parameterDiscoveryAudit/AuditActionButtons';
import LegacyBackendAiActions from './parameterDiscoveryAudit/LegacyBackendAiActions';
import AiIssueTriageRunSection from './parameterDiscoveryAudit/AiIssueTriageRunSection';
import AiCandidateCurationRunSection from './parameterDiscoveryAudit/AiCandidateCurationRunSection';
import SourceReportSection from './parameterDiscoveryAudit/SourceReportSection';
import FeatureAuditSessionSection from './parameterDiscoveryAudit/FeatureAuditSessionSection';
import BackendRepairSessionSection from './parameterDiscoveryAudit/BackendRepairSessionSection';
import ScorecardGenerationSection from './parameterDiscoveryAudit/ScorecardGenerationSection';
import type { ObjectiveMode, RealizedOutcomeMaterializationEntityType } from './parameterDiscovery/ObjectiveModeControl';
import ScorecardDraftSection from './parameterDiscoveryAudit/ScorecardDraftSection';
import TopDraftRulesSection from './parameterDiscoveryAudit/TopDraftRulesSection';
import BackendIssueQueueSection from './parameterDiscoveryAudit/BackendIssueQueueSection';
import { useParameterDiscoveryAuditQueries } from './parameterDiscoveryAudit/useParameterDiscoveryAuditQueries';
import { toolLaunchUrls } from './parameterDiscoveryAudit/constants';
import {
    formatCount,
    getAuditScopeLabel,
    getDefaultAuditScope,
    getMetricsForAuditScope,
    getScorecardGenerationToolLabel
} from './parameterDiscoveryAudit/helpers';
import type {
    AuditMetricScope,
    FeatureAuditQueueScope,
    ParameterDiscoveryMetric,
    ScorecardGenerationCandidateScope,
    ScorecardGenerationExternalTool
} from './parameterDiscoveryAudit/types';

interface ParameterDiscoveryAuditPanelProps {
    enabled: boolean;
    experimentId: number;
    duration: string;
    horizonSeconds: number;
    metric: ParameterDiscoveryMetric;
    hasOptionMetricData?: boolean;
    strategyId?: number | null;
    materializationEntityType?: RealizedOutcomeMaterializationEntityType | null;
    mode?: 'admin' | 'scorecards' | 'all';
    onMetricChange: (metric: ParameterDiscoveryMetric) => void;
}

const ParameterDiscoveryAuditPanel = ({
    enabled,
    experimentId,
    duration,
    horizonSeconds,
    metric,
    hasOptionMetricData,
    strategyId = null,
    materializationEntityType = null,
    mode = 'all',
    onMetricChange
}: ParameterDiscoveryAuditPanelProps) => {
    const [createdScorecardLink, setCreatedScorecardLink] = useState<{ scorecardId: number; strategyId: number } | null>(null);
    const defaultAuditScope = getDefaultAuditScope(metric, hasOptionMetricData);
    const auditScopeKey = `${experimentId}:${duration}:${horizonSeconds}:${metric}:${hasOptionMetricData ? 'options' : 'stock'}`;
    const [auditScopeSelection, setAuditScopeSelection] = useState<{ key: string; value: AuditMetricScope } | null>(null);
    const [scorecardGenerationTool, setScorecardGenerationTool] = useState<ScorecardGenerationExternalTool>('codex');
    const [scorecardGenerationScope, setScorecardGenerationScope] = useState<ScorecardGenerationCandidateScope>('all_diagnostics');
    const [scorecardObjectiveMode, setScorecardObjectiveMode] = useState<ObjectiveMode>('fixed_horizon');
    const [scorecardRealizedOutcomeSetId, setScorecardRealizedOutcomeSetId] = useState<number | null>(null);
    const [featureAuditQueueScope, setFeatureAuditQueueScope] = useState<FeatureAuditQueueScope>('needs_review');
    const [selectedFeatureAuditSessionId, setSelectedFeatureAuditSessionId] = useState<number | null>(null);
    const [selectedBackendRepairSessionId, setSelectedBackendRepairSessionId] = useState<number | null>(null);
    const [selectedGenerationSessionId, setSelectedGenerationSessionId] = useState<number | null>(null);
    const auditScope = auditScopeSelection?.key === auditScopeKey ? auditScopeSelection.value : defaultAuditScope;
    const scorecardMetric = metric;
    const scorecardMetricUnavailable = scorecardMetric === 'options' && hasOptionMetricData === false;
    const metrics = useMemo<ParameterDiscoveryMetric[]>(() => getMetricsForAuditScope(auditScope), [auditScope]);
    const scorecardOutcomeSetsQuery = useQuery({
        ...trpc.experiment.listReadyRealizedOutcomeSets.queryOptions({ experimentId, duration, horizonSeconds }),
        enabled
    });
    const selectedScorecardOutcomeSet =
        scorecardOutcomeSetsQuery.data?.outcomeSets.find((set) => set.id === scorecardRealizedOutcomeSetId) ?? null;
    const scorecardObjectiveEvidenceReady =
        scorecardObjectiveMode === 'fixed_horizon' || selectedScorecardOutcomeSet?.discoveryStatus === 'evidence_ready';

    const requireScorecardObjectiveEvidence = () => {
        if (!scorecardObjectiveEvidenceReady) {
            throw new Error('Prepare realized parameter discovery before generating a scorecard.');
        }
    };

    const handleScorecardMetricChange = (value: ParameterDiscoveryMetric) => {
        setCreatedScorecardLink(null);
        setSelectedGenerationSessionId(null);
        onMetricChange(value);
    };

    const handleScorecardObjectiveModeChange = (value: ObjectiveMode) => {
        setCreatedScorecardLink(null);
        setSelectedGenerationSessionId(null);
        setScorecardObjectiveMode(value);
    };

    const handleScorecardRealizedOutcomeSetIdChange = (value: number | null) => {
        setCreatedScorecardLink(null);
        setSelectedGenerationSessionId(null);
        setScorecardRealizedOutcomeSetId(value);
    };

    const handleAuditScopeChange = (value: AuditMetricScope) => {
        setAuditScopeSelection({ key: auditScopeKey, value });
        setSelectedFeatureAuditSessionId(null);
        setSelectedBackendRepairSessionId(null);
        if (value !== 'both') handleScorecardMetricChange(value);
    };

    const {
        sourceReportInput,
        aiIssueTriageRunInput,
        aiCandidateCurationRunInput,
        aiIssueTriageRunQuery,
        aiCandidateCurationRunQuery,
        aiBackendTriageRunning,
        aiCandidateCurationRunning,
        sourceReportQuery,
        scorecardDraftQuery,
        backendIssuesQuery,
        scorecardGenerationSessionsQuery,
        featureAuditSessionsQuery,
        backendRepairSessionsQuery,
        selectedMcpReadiness,
        featureAuditSessions,
        scorecardGenerationSessions,
        activeFeatureAuditSessionId,
        activeBackendRepairSessionId,
        activeGenerationSessionId,
        featureAuditSessionQuery,
        scorecardGenerationSessionQuery,
        backendRepairSessionQuery,
        refreshAuditQueries
    } = useParameterDiscoveryAuditQueries({
        enabled: enabled && !scorecardMetricUnavailable,
        experimentId,
        duration,
        horizonSeconds,
        metrics,
        scorecardMetric,
        objectiveMode: scorecardObjectiveMode,
        objectiveEvidenceReady: scorecardObjectiveEvidenceReady,
        realizedOutcomeSetId: scorecardRealizedOutcomeSetId,
        auditScope,
        scorecardGenerationTool,
        selectedFeatureAuditSessionId,
        selectedBackendRepairSessionId,
        selectedGenerationSessionId
    });

    const populateLedgerMutation = useMutation({
        mutationFn: () =>
            trpcClient.experiment.populateSourceAuditLedger.mutate({
                ...sourceReportInput,
                includeReport: true
            }),
        onSuccess: () => {
            showToast('Audit status refreshed', 'success');
            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to refresh audit status';
            showToast(message, 'error');
        }
    });

    const deterministicAuditMutation = useMutation({
        mutationFn: () =>
            trpcClient.experiment.auditFeatureDiagnostics.mutate({
                ...sourceReportInput,
                deterministicCuration: true,
                enqueueIssues: true,
                includeReport: true
            }),
        onSuccess: () => {
            showToast('Feature diagnostics audit completed', 'success');
            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to audit feature diagnostics';
            showToast(message, 'error');
        }
    });

    const aiBackendTriageMutation = useMutation({
        mutationFn: () => trpcClient.experiment.startAiIssueTriageRun.mutate(aiIssueTriageRunInput),
        onSuccess: (result) => {
            const message = result.started
                ? 'Full AI backend issue triage started'
                : `AI backend issue triage is already ${result.run?.status ?? 'active'}`;

            showToast(message, 'success');
            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to run AI backend triage';
            showToast(message, 'error');
        }
    });

    const aiCandidateCurationMutation = useMutation({
        mutationFn: () => trpcClient.experiment.startAiCandidateCurationRun.mutate(aiCandidateCurationRunInput),
        onSuccess: (result) => {
            const message = result.started
                ? 'Full AI candidate curation started'
                : `AI candidate curation is already ${result.run?.status ?? 'active'}`;

            showToast(message, 'success');
            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to run AI scorecard curation';
            showToast(message, 'error');
        }
    });

    const startFeatureAuditSessionMutation = useMutation({
        mutationFn: () =>
            trpcClient.experiment.startParameterDiscoveryFeatureAuditSession.mutate({
                experimentId,
                duration,
                horizonSeconds,
                metrics,
                externalTool: scorecardGenerationTool,
                queueScope: featureAuditQueueScope,
                limit: 10000
            }),
        onSuccess: (result) => {
            setSelectedFeatureAuditSessionId(result.session.id);
            const connectionLabel = result.workbench.connectionReadiness.label;

            showToast(
                `External feature audit session ${result.session.id} created with ${formatCount(result.session.totalItemCount)} item(s) (${connectionLabel})`,
                'success'
            );

            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to start external feature audit';
            showToast(message, 'error');
        }
    });

    const startBackendRepairSessionMutation = useMutation({
        mutationFn: () =>
            trpcClient.experiment.startBackendFeatureRepairSession.mutate({
                experimentId,
                duration,
                horizonSeconds,
                metrics,
                statuses: ['codex_queued'],
                externalTool: scorecardGenerationTool,
                limit: 500
            }),
        onSuccess: (result) => {
            setSelectedBackendRepairSessionId(result.session.id);
            const connectionLabel = result.workbench.connectionReadiness.label;
            showToast(`Backend repair session ${result.session.id} created (${connectionLabel})`, 'success');
            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to start backend repair session';
            showToast(message, 'error');
        }
    });

    const startScorecardGenerationMutation = useMutation({
        mutationFn: () => {
            requireScorecardObjectiveEvidence();

            return trpcClient.experiment.startScorecardGenerationSession.mutate({
                experimentId,
                duration,
                horizonSeconds,
                metric: scorecardMetric,
                externalTool: scorecardGenerationTool,
                candidateScope: scorecardGenerationScope,
                ...(scorecardObjectiveMode === 'strategy_exit'
                    ? { objectiveMode: scorecardObjectiveMode, realizedOutcomeSetId: scorecardRealizedOutcomeSetId }
                    : {})
            });
        },
        onSuccess: (result) => {
            setSelectedGenerationSessionId(result.session.id);
            const connectionLabel = result.workbench?.connectionReadiness?.label ?? 'MCP status unknown';
            showToast(`External AI session ${result.session.id} created (${connectionLabel})`, 'success');

            if (result.externalAuditCoverage?.warning) {
                showToast(result.externalAuditCoverage.warning, 'warning');
            }

            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to start AI scorecard generation';
            showToast(message, 'error');
        }
    });

    const createScorecardMutation = useMutation({
        mutationFn: () => {
            requireScorecardObjectiveEvidence();

            return trpcClient.experiment.createScorecardFromDraft.mutate({
                experimentId,
                duration,
                horizonSeconds,
                metric: scorecardMetric,
                ...(scorecardObjectiveMode === 'strategy_exit'
                    ? { objectiveMode: scorecardObjectiveMode, realizedOutcomeSetId: scorecardRealizedOutcomeSetId }
                    : { objectiveMode: scorecardObjectiveMode }),
                maxRules: 8,
                maxRulesPerFamily: 2,
                extractBaselines: false,
                runtimeQualityGate: false
            });
        },
        onSuccess: (result) => {
            setCreatedScorecardLink(
                result.scorecard ? { scorecardId: result.scorecard.id, strategyId: result.experiment.strategyId } : null
            );

            showToast(`Created scorecard ${result.scorecard?.id ?? ''}`.trim(), 'success');
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to create scorecard from draft';
            showToast(message, 'error');
        }
    });

    const createGenerationScorecardMutation = useMutation({
        mutationFn: (draftId: number) =>
            trpcClient.experiment.createScorecardFromGenerationDraft.mutate({
                sessionId: activeGenerationSessionId ?? 0,
                draftId
            }),
        onSuccess: (result) => {
            const strategyId =
                result.strategyVariant.status === 'created'
                    ? result.strategyVariant.strategyId
                    : (scorecardGenerationSessionQuery.data?.session.strategyId ?? 0);
            setCreatedScorecardLink({ scorecardId: result.scorecard.id, strategyId });
            const requiresManualOptionAcknowledgment = scorecardMetric === 'options' && scorecardObjectiveMode === 'fixed_horizon';
            showToast(
                requiresManualOptionAcknowledgment
                    ? `Created locked AI scorecard ${result.scorecard.id} with its validated thresholds. After baselines are ready, review and attach it to acknowledge the fixed-horizon option proxy.`
                    : `Created locked AI scorecard ${result.scorecard.id} with its validated thresholds. Baselines are preparing; it will attach to the inactive variant automatically.`,
                'success'
            );
            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to create scorecard from AI draft';
            showToast(message, 'error');
        }
    });

    const decideAIWorkApprovalMutation = useMutation({
        mutationFn: ({ approvalId, status }: { approvalId: number; status: 'approved' | 'rejected' }) =>
            trpcClient.experiment.decideAIWorkApproval.mutate({
                approvalId,
                status
            }),
        onSuccess: (approval) => {
            showToast(`AI approval ${approval.status}`, 'success');
            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to update AI approval';
            showToast(message, 'error');
        }
    });

    const cancelGenerationSessionMutation = useMutation({
        mutationFn: ({ sessionId, reason }: { sessionId: number; reason: string }) =>
            trpcClient.experiment.cancelScorecardGenerationSession.mutate({
                sessionId,
                reason
            }),
        onSuccess: (result) => {
            const { releasedFeatureClaimCount } = result.cancellation;

            showToast(
                releasedFeatureClaimCount > 0
                    ? `Cancelled AI scorecard session #${result.session.id} and released ${releasedFeatureClaimCount} claimed feature(s)`
                    : `Cancelled AI scorecard session #${result.session.id}`,
                'success'
            );
            refreshAuditQueries();
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to cancel AI scorecard session';
            showToast(message, 'error');
        }
    });

    const handleCopyFeatureAuditPrompt = async () => {
        const prompt = featureAuditSessionQuery.data?.session?.prompt;
        if (!prompt) return;

        if (!navigator.clipboard) {
            showToast('Clipboard is unavailable', 'error');
            return;
        }

        await navigator.clipboard.writeText(prompt);
        showToast('External feature-audit prompt copied', 'success');
    };

    const handleCopyBackendRepairPrompt = async () => {
        const prompt = backendRepairSessionQuery.data?.session?.prompt;
        if (!prompt) return;

        if (!navigator.clipboard) {
            showToast('Clipboard is unavailable', 'error');
            return;
        }

        await navigator.clipboard.writeText(prompt);
        showToast('Backend repair prompt copied', 'success');
    };

    const handleCopyGenerationPrompt = async () => {
        const prompt = scorecardGenerationSessionQuery.data?.agentPrompt;
        if (!prompt) return;

        if (!navigator.clipboard) {
            showToast('Clipboard is unavailable', 'error');
            return;
        }

        await navigator.clipboard.writeText(prompt);
        showToast('External agent prompt copied', 'success');
    };

    const handleOpenExternalTool = () => {
        const launchUrl = toolLaunchUrls[scorecardGenerationTool];

        if (!launchUrl) {
            showToast(`${getScorecardGenerationToolLabel(scorecardGenerationTool)} cannot be opened directly from the browser`, 'error');
            return;
        }

        window.open(launchUrl, '_blank', 'noopener,noreferrer');
    };

    if (!enabled) return null;

    const report = sourceReportQuery.data;
    const draft = scorecardDraftQuery.data;
    const topRules = draft?.rules.slice(0, 6) ?? [];

    const actionPending =
        populateLedgerMutation.isPending ||
        deterministicAuditMutation.isPending ||
        aiBackendTriageMutation.isPending ||
        aiCandidateCurationMutation.isPending ||
        aiCandidateCurationRunning ||
        startFeatureAuditSessionMutation.isPending ||
        startBackendRepairSessionMutation.isPending ||
        startScorecardGenerationMutation.isPending ||
        decideAIWorkApprovalMutation.isPending ||
        cancelGenerationSessionMutation.isPending ||
        createGenerationScorecardMutation.isPending ||
        createScorecardMutation.isPending;
    const scorecardActionPending = actionPending || scorecardMetricUnavailable;

    const scopeSummary = `${duration} at ${formatTimeHorizon(horizonSeconds)} • ${
        metric === 'options' ? 'Options' : 'Stock'
    } authoring • ${getAuditScopeLabel(auditScope)} • ${scorecardMetric === 'options' ? 'Options' : 'Stock'} scorecard`;

    const diagnosticsContent = (
        <div className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <AuditScopeControl disabled={actionPending} value={auditScope} onChange={handleAuditScopeChange} />
                <AuditActionButtons
                    actionPending={actionPending}
                    populateLedgerPending={populateLedgerMutation.isPending}
                    runAuditPending={deterministicAuditMutation.isPending}
                    onPopulateLedger={() => populateLedgerMutation.mutate()}
                    onRunAudit={() => deterministicAuditMutation.mutate()}
                />
            </div>
            <SourceReportSection errorMessage={sourceReportQuery.error?.message} isLoading={sourceReportQuery.isLoading} report={report} />
        </div>
    );

    const aiRunsContent = (
        <div className="space-y-4">
            <AiIssueTriageRunSection
                errorMessage={aiIssueTriageRunQuery.error?.message}
                isLoading={aiIssueTriageRunQuery.isLoading}
                snapshot={aiIssueTriageRunQuery.data}
            />

            <AiCandidateCurationRunSection
                errorMessage={aiCandidateCurationRunQuery.error?.message}
                isLoading={aiCandidateCurationRunQuery.isLoading}
                snapshot={aiCandidateCurationRunQuery.data}
            />

            <LegacyBackendAiActions
                actionPending={actionPending}
                aiBackendTriagePending={aiBackendTriageMutation.isPending}
                aiBackendTriageRunning={aiBackendTriageRunning}
                aiCandidateCurationPending={aiCandidateCurationMutation.isPending}
                aiCandidateCurationRunning={aiCandidateCurationRunning}
                onAiBackendTriage={() => aiBackendTriageMutation.mutate()}
                onAiCandidateCuration={() => aiCandidateCurationMutation.mutate()}
            />
        </div>
    );

    const externalAuditContent = (
        <FeatureAuditSessionSection
            actionPending={actionPending}
            auditScope={auditScope}
            details={featureAuditSessionQuery.data}
            errorMessage={featureAuditSessionsQuery.error?.message ?? featureAuditSessionQuery.error?.message}
            isLoadingDetails={featureAuditSessionQuery.isLoading}
            isLoadingSessions={featureAuditSessionsQuery.isLoading}
            isStarting={startFeatureAuditSessionMutation.isPending}
            queueScope={featureAuditQueueScope}
            readiness={selectedMcpReadiness}
            selectedSessionId={activeFeatureAuditSessionId}
            sessions={featureAuditSessions}
            tool={scorecardGenerationTool}
            onCopyPrompt={handleCopyFeatureAuditPrompt}
            onOpenExternalTool={handleOpenExternalTool}
            onQueueScopeChange={setFeatureAuditQueueScope}
            onSelectSession={setSelectedFeatureAuditSessionId}
            onStart={() => startFeatureAuditSessionMutation.mutate()}
            onToolChange={setScorecardGenerationTool}
        />
    );

    const scorecardsContent = (
        <div className="space-y-4">
            {scorecardMetricUnavailable && (
                <div className="rounded border border-amber-800/70 bg-amber-950/20 p-3 text-sm text-amber-200">
                    Options scorecard data is unavailable for this experiment. Select Stock, or rerun the experiment with options results
                    enabled.
                </div>
            )}
            <ScorecardGenerationSection
                actionPending={scorecardActionPending}
                candidateScope={scorecardGenerationScope}
                details={scorecardGenerationSessionQuery.data}
                duration={duration}
                horizonSeconds={horizonSeconds}
                errorMessage={scorecardGenerationSessionsQuery.error?.message ?? scorecardGenerationSessionQuery.error?.message}
                experimentId={experimentId}
                hasOptionMetricData={hasOptionMetricData}
                isCreatingScorecard={createGenerationScorecardMutation.isPending}
                isCancellingSession={cancelGenerationSessionMutation.isPending}
                isDecidingApproval={decideAIWorkApprovalMutation.isPending}
                isLoadingDetails={scorecardGenerationSessionQuery.isLoading}
                isLoadingSessions={scorecardGenerationSessionsQuery.isLoading}
                isStarting={startScorecardGenerationMutation.isPending}
                materializationEntityType={materializationEntityType}
                objectiveMode={scorecardObjectiveMode}
                objectiveEvidenceReady={scorecardObjectiveEvidenceReady}
                readiness={selectedMcpReadiness}
                realizedOutcomeSetId={scorecardRealizedOutcomeSetId}
                scorecardMetric={scorecardMetric}
                selectedSessionId={activeGenerationSessionId}
                sessions={scorecardGenerationSessions}
                strategyId={strategyId}
                tool={scorecardGenerationTool}
                onCopyPrompt={handleCopyGenerationPrompt}
                onCreateScorecard={(draftId) => createGenerationScorecardMutation.mutate(draftId)}
                onCancelSession={(sessionId, reason, onCancelled) =>
                    cancelGenerationSessionMutation.mutate({ sessionId, reason }, { onSuccess: onCancelled })
                }
                onDecideApproval={(approvalId, status) => decideAIWorkApprovalMutation.mutate({ approvalId, status })}
                onObjectiveModeChange={handleScorecardObjectiveModeChange}
                onOpenExternalTool={handleOpenExternalTool}
                onRealizedOutcomeSetIdChange={handleScorecardRealizedOutcomeSetIdChange}
                onScopeChange={setScorecardGenerationScope}
                onScorecardMetricChange={handleScorecardMetricChange}
                onSelectSession={setSelectedGenerationSessionId}
                onStart={() => startScorecardGenerationMutation.mutate()}
                onToolChange={setScorecardGenerationTool}
            />

            <div className="grid gap-4 lg:grid-cols-[1fr_1.3fr]">
                <ScorecardDraftSection
                    actionPending={scorecardActionPending}
                    createdScorecardLink={createdScorecardLink}
                    draft={draft}
                    duration={duration}
                    horizonSeconds={horizonSeconds}
                    errorMessage={scorecardDraftQuery.error?.message}
                    experimentId={experimentId}
                    hasOptionMetricData={hasOptionMetricData}
                    isCreating={createScorecardMutation.isPending}
                    isLoading={scorecardDraftQuery.isLoading}
                    objectiveMode={scorecardObjectiveMode}
                    objectiveEvidenceReady={scorecardObjectiveEvidenceReady}
                    realizedOutcomeSetId={scorecardRealizedOutcomeSetId}
                    scorecardMetric={scorecardMetric}
                    onCreateScorecard={() => createScorecardMutation.mutate()}
                    onObjectiveModeChange={handleScorecardObjectiveModeChange}
                    onRealizedOutcomeSetIdChange={handleScorecardRealizedOutcomeSetIdChange}
                    onScorecardMetricChange={handleScorecardMetricChange}
                />

                <TopDraftRulesSection rules={topRules} />
            </div>
        </div>
    );

    const backendRepairContent = (
        <BackendRepairSessionSection
            actionPending={actionPending}
            auditScope={auditScope}
            details={backendRepairSessionQuery.data}
            errorMessage={backendRepairSessionsQuery.error?.message ?? backendRepairSessionQuery.error?.message}
            isLoadingDetails={backendRepairSessionQuery.isLoading}
            isLoadingSessions={backendRepairSessionsQuery.isLoading}
            isStarting={startBackendRepairSessionMutation.isPending}
            readiness={selectedMcpReadiness}
            selectedSessionId={activeBackendRepairSessionId}
            sessions={backendRepairSessionsQuery.data}
            tool={scorecardGenerationTool}
            onCopyPrompt={handleCopyBackendRepairPrompt}
            onOpenExternalTool={handleOpenExternalTool}
            onSelectSession={setSelectedBackendRepairSessionId}
            onStart={() => startBackendRepairSessionMutation.mutate()}
        />
    );

    const issueQueueContent = (
        <BackendIssueQueueSection
            errorMessage={backendIssuesQuery.error?.message}
            isLoading={backendIssuesQuery.isLoading}
            issues={backendIssuesQuery.data}
        />
    );

    const adminTabs: Tab[] = [
        { id: 'diagnostics', label: 'Diagnostics', content: diagnosticsContent },
        { id: 'external-audit', label: 'External AI Audit', content: externalAuditContent },
        { id: 'ai-runs', label: 'AI Runs', content: aiRunsContent },
        { id: 'backend-repair', label: 'Backend Repair', content: backendRepairContent },
        { id: 'issue-queue', label: 'Issue Queue', content: issueQueueContent }
    ];

    const allTabs: Tab[] = [...adminTabs, { id: 'scorecards', label: 'Scorecards', content: scorecardsContent }];
    const title = mode === 'scorecards' ? 'Scorecard Workbench' : mode === 'admin' ? 'Admin Workbench' : 'Parameter Discovery Workbench';
    const icon = mode === 'scorecards' ? <FaMagic className="text-blue-400" /> : <FaClipboardCheck className="text-blue-400" />;

    return (
        <Card responsivePadding className="bg-zinc-900">
            <div className="space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            {icon}
                            <h3 className="text-lg font-semibold text-white">{title}</h3>
                        </div>
                        <div className="mt-1 text-sm text-zinc-500">{scopeSummary}</div>
                    </div>
                </div>

                {mode === 'scorecards' ? (
                    scorecardsContent
                ) : mode === 'admin' ? (
                    <Tabs smallTabs contentClassName="mt-4" tabs={adminTabs} />
                ) : (
                    <Tabs smallTabs contentClassName="mt-4" tabs={allTabs} />
                )}
            </div>
        </Card>
    );
};

export default ParameterDiscoveryAuditPanel;
